import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { IMPACT_REPORT_DEFAULT_VIEW_MODE } from "@/constants/impactReport";
import { usePermissions } from "@/hooks/usePermissions";
import type {
  CauseSection,
  ImpactCauseOption,
  ImpactReportContextValue,
  ImpactReportControls,
  ImpactViewMode,
} from "@/interfaces/impactReport";
import {
  buildImpactReportFilter,
  canRequestImpactReport,
  capImpactCampaignSelection,
  clearImpactReportControls,
  hasActiveImpactFilters,
  hasImpactCauseSelection,
} from "@/utils/impact-report-filter-helper";

/* ─────────────────────────────────────────────────────────────────────────────────────────────
   THE CONTROL STATE, shared across the router outlet.

   The control bar renders inside `Drawer.tsx`'s `Toolbar`, which sits ABOVE `<Outlet />`; the
   renderers live in `pages/ImpactReport.tsx`, BELOW it. Neither can own state the other reads, so
   it lives here and the provider is mounted around `<Drawer />` in `App.tsx` — the same shape
   `CampaignBuilderProvider` uses for the campaign flow.

   HOLDS STATE, FETCHES NOTHING. Mounting it app-wide therefore costs every other route one
   `useState` triple and a memo, and issues no request on any route including this one.

   THE REFETCH BOUNDARY, which is this slice's whole subject: `viewMode` and `filter` are two
   separate members and are never combined. `filter` is the only one that reaches
   `useImpactReport`; `viewMode` cannot reach it even by accident, because
   `buildImpactReportFilter` has no parameter for it. Nothing here invalidates, refetches or
   resets a query — the provider does not import `useQueryClient` at all.
   ───────────────────────────────────────────────────────────────────────────────────────────── */

const ImpactReportContext = createContext<ImpactReportContextValue | null>(null);

/** Whether two option lists name the same causes, in the same order, with the same labels. */
function isSameCauseOptionList(
  previous: readonly ImpactCauseOption[],
  next: readonly ImpactCauseOption[],
): boolean {
  return (
    previous.length === next.length &&
    previous.every(
      (option, index) =>
        option.causeValue === next[index]?.causeValue &&
        option.causeName === next[index]?.causeName,
    )
  );
}

export function ImpactReportProvider({ children }: { children: ReactNode }) {
  // "Cleared" and "untouched" are the same selections, so the helper that clears also seeds.
  // Passed as an initialiser rather than called inline: it returns fresh arrays every time, and
  // building a throwaway set on every render would be pure waste.
  const [controls, setControls] = useState<ImpactReportControls>(clearImpactReportControls);
  const [viewMode, setViewMode] = useState<ImpactViewMode>(IMPACT_REPORT_DEFAULT_VIEW_MODE);
  const [causeOptions, setCauseOptions] = useState<readonly ImpactCauseOption[]>([]);

  // THE one composed filter, memoised on `controls` alone. Referential stability is not a
  // micro-optimisation here: the control bar and the page each hand this object to
  // `useImpactReport`, and it is what makes those two calls ONE query rather than two.
  const filter = useMemo(() => buildImpactReportFilter(controls), [controls]);

  // The capped view of the selection, so the bar and the page can both say "250 of 312" without
  // either re-deriving the cap. `buildImpactReportFilter` caps independently for the wire; the two
  // agree by construction because both delegate to the same helper on the same input.
  const campaignSelection = useMemo(
    () => capImpactCampaignSelection(controls.campaignIds),
    [controls.campaignIds],
  );

  // Only the PRESENCE of a cause selection matters, never which causes — so the refresh callback's
  // identity survives a change of selection within a cause-filtered state. The hero's summary page
  // asks this same question, and reads the same value off the context below rather than a second
  // copy of the expression.
  const hasCauseSelection = hasImpactCauseSelection(controls);

  // `isGfg` reads false until permissions load, so `isLoaded` is half of the gate: without it a
  // GFG staff session would fire the org-wide request the upstream refuses them.
  const { isGfg, isLoaded: arePermissionsLoaded } = usePermissions();
  const allowOrgScope = arePermissionsLoaded && !isGfg;

  const refreshCauseOptions = useCallback(
    (causeData: readonly CauseSection[] | undefined) => {
      // The narrowing problem: a cause filter narrows `causeData`, so a payload fetched WITH one
      // lists only the causes already selected. Refreshing from it would shrink the menu to the
      // current selection and leave the user no way back to the ones they filtered out. Campaign
      // and date filters may narrow it, and should — those genuinely change what is in scope.
      if (hasCauseSelection || causeData === undefined) return;

      setCauseOptions((previous) => {
        const next = causeData.map(({ causeValue, causeName }) => ({ causeValue, causeName }));
        // Same causes as last time → keep the previous array. A fresh array on every settled
        // payload would re-render every consumer of this context for no visible change.
        return isSameCauseOptionList(previous, next) ? previous : next;
      });
    },
    [hasCauseSelection],
  );

  // The collapsed "N campaigns" pill and the page's empty state both open the campaign picker
  // dialog, whose open state is private to the control bar. Clicking the CAMPAIGN control's REAL
  // button (rather than lifting the open state out, or duplicating it at each caller) keeps one
  // place that opens it — see `ImpactFilterMenuProps.triggerButtonRef`.
  const campaignMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const openCampaignMenu = useCallback(() => {
    campaignMenuTriggerRef.current?.click();
  }, []);

  // The empty state's "widen the date"/"clear the cause" actions FOCUS these (never click — that
  // would pop the menu open) before calling `setControls`, so focus lands on a control the app bar
  // keeps mounted instead of the button `setControls` is about to unmount underneath the caller —
  // see `pages/ImpactReport.tsx`'s `onAction` map. Same ownership reasoning as
  // `campaignMenuTriggerRef`: only the provider is reachable from both the bar (above the outlet)
  // and the page (below it).
  const causeMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const dateMenuTriggerRef = useRef<HTMLButtonElement>(null);

  const value = useMemo<ImpactReportContextValue>(
    () => ({
      controls,
      setControls,
      viewMode,
      setViewMode,
      filter,
      // Three different questions, never interchangeable — see the three predicates in the helper.
      // `canRequestReport` gates the REQUEST (a scope, or org-wide); `hasActiveFilters` drives the
      // PILLS and clear-all (the user selected something visible, campaigns included), and is true for a
      // cause-only selection that must still never be requested; `hasCauseSelection` drives the
      // hero's SUMMARY PAGE and counts causes ALONE, so a date-filtered whole-account report keeps
      // the cover that describes the account.
      canRequestReport: canRequestImpactReport(controls, { allowOrgScope }),
      hasActiveFilters: hasActiveImpactFilters(controls),
      hasCauseSelection,
      campaignSelection,
      causeOptions,
      refreshCauseOptions,
      campaignMenuTriggerRef,
      openCampaignMenu,
      causeMenuTriggerRef,
      dateMenuTriggerRef,
    }),
    [
      controls,
      allowOrgScope,
      viewMode,
      filter,
      hasCauseSelection,
      campaignSelection,
      causeOptions,
      refreshCauseOptions,
      openCampaignMenu,
    ],
  );

  return <ImpactReportContext.Provider value={value}>{children}</ImpactReportContext.Provider>;
}

/**
 * The control state, or a thrown error naming the missing provider.
 *
 * Thrown rather than defaulted: a silent default would let the app bar's controls and the page's
 * renderers each hold their own private copy of the state, which looks like a working bar whose
 * selections change nothing — a far worse failure than a stack trace naming the mount point.
 */
export function useImpactReportContext(): ImpactReportContextValue {
  const context = useContext(ImpactReportContext);
  if (context === null) {
    throw new Error("useImpactReportContext must be used within an <ImpactReportProvider>");
  }
  return context;
}
